import { isValidCountryCode } from 'src/utils/country';
import {
  BuyerWithSalesDto,
  CreateRetailSaleGroupInput,
  CreateRetailSaleInput,
  EventWithData,
  ListingDetails,
  MarketplacePaymentLineType,
  MarketplacePaymentState,
  PointOfSaleSaleStatus,
  RetailSaleLineItemInput,
  RetailSaleListingTickets,
  SaleCancellationReason,
  SaleLineItemCoverage,
  Ticket,
  TicketType,
} from 'src/WebApiController';

import {
  MultiListingSelectionRow,
  RetailSaleFormValues,
  RetailSalePaymentLine,
} from './types';

const roundTo2 = (n: number) => Math.round(n * 100) / 100;

/**
 * Taxable base for the auto-generated buyer tax line (POS-8561): the total net proceeds plus the
 * net of the manual charges/credits. The managed Tax line itself is excluded so the calculation is
 * idempotent when the recomputed line is written back into `marketplacePaymentLines`.
 */
export const getRetailTaxableAmount = (
  totalNetProceeds: number | null | undefined,
  marketplacePaymentLines: RetailSalePaymentLine[] | null | undefined
): number => {
  const manualTotal = (marketplacePaymentLines ?? [])
    .filter((l) => l.paymentLineType !== MarketplacePaymentLineType.Tax)
    .reduce((sum, l) => sum + (l.amount ?? 0), 0);
  return (totalNetProceeds ?? 0) + manualTotal;
};

/** Tax amount = rate (a decimal fraction, e.g. 0.0825) × taxable base, rounded to cents. */
export const computeRetailTaxAmount = (
  taxRate: number,
  taxableAmount: number
): number => roundTo2(taxRate * taxableAmount);

/**
 * Resolves the event virtId whose venue location drives the retail tax-rate lookup (POS-8561).
 * Priority:
 *   1. the context event — supplied directly by the single-listing flow (listing detail), where
 *      there is no multi-selection and no listing-tickets fallback (POS-9097);
 *   2. the first multi-selected event (multi-listing via inventory selection);
 *   3. the first listing-tickets row's viagVirtId (multi-listing fallback query).
 * Returns undefined/null when none is available, which disables the tax-rate query (rate 0).
 */
export const resolveTaxVenueVirtId = (
  contextEvent: EventWithData | undefined,
  selectedEvents: EventWithData[] | undefined,
  listingDetails: RetailSaleListingTickets[] | undefined
): string | null | undefined =>
  contextEvent?.event?.virtId ??
  selectedEvents?.[0]?.event?.virtId ??
  listingDetails?.[0]?.viagVirtId;

export const getRetailExternalSaleId = (listingId: number) => {
  return `Retail-${listingId}-${Date.now()}`;
};

export const getRetailMerchantReferenceId = (saleId: number) => {
  return `Retail-${saleId}-${Date.now()}`;
};

/** Inclusive seat-range check; numeric when both bounds and the seat are numeric, else lexicographic. */
const isSeatInRange = (
  seat: string | null | undefined,
  from: string | null | undefined,
  to: string | null | undefined
): boolean => {
  if (!from && !to) return true;
  const s = (seat ?? '').trim();
  const asNum = (v: string) => (/^\d+$/.test(v) ? Number(v) : null);
  const sn = asNum(s);
  const fn = from ? asNum(from) : null;
  const tn = to ? asNum(to) : null;

  // Numeric comparison when everything present is numeric.
  if (sn != null && (!from || fn != null) && (!to || tn != null)) {
    if (fn != null && sn < fn) return false;
    if (tn != null && sn > tn) return false;
    return true;
  }

  // Fallback to lexicographic comparison.
  if (from && s < from) return false;
  if (to && s > to) return false;
  return true;
};

/**
 * Infers the ticket ids to sell from the form's section/row/seat selection: the listing's available
 * tickets in the selected row whose seat falls within [seatFrom, seatTo]. Falls back to the first
 * quantitySold available tickets when no seat range is given (e.g. GA). Available = not sold, held,
 * spoiled, or wasted.
 */
export const resolveSaleTicketIds = (
  listing: ListingDetails | undefined,
  values: RetailSaleFormValues
): number[] => {
  const available = (listing?.tickets ?? []).filter(
    (t: Ticket) =>
      t.saleId == null &&
      t.holdId == null &&
      t.spoiledSaleId == null &&
      !t.isWasted
  );

  let matched = available;
  if (values.row) {
    matched = matched.filter((t) => (t.row ?? '') === values.row);
  }
  if (values.seatFrom || values.seatTo) {
    matched = matched.filter((t) =>
      isSeatInRange(t.seat, values.seatFrom, values.seatTo)
    );
  }

  const ids = matched.map((t) => t.id);
  const quantity = values.quantitySold ?? ids.length;
  return ids.slice(0, quantity);
};

/**
 * Multi-listing mode (POS-8649): derive the flat TicketIds set from the
 * per-row selections. When `seatFrom`/`seatTo` are set, filters the
 * available tickets to only those in the selected seat range (using the
 * parallel `availableTicketSeats` array), then takes the first `qtyToSell`.
 * When no seat range is set (e.g. GA), falls back to the first `qtyToSell`
 * tickets in listing ordinal order. Rows with qty=0 contribute nothing.
 *
 * The result is deduplicated because the backend contract is `ISet<long>` —
 * `SaleClient.createRetailSaleGroup` rejects duplicate ticket IDs with an
 * "item with the same key has already been added" error. Overlaps can leak
 * in when upstream data has anomalies (e.g. the same listing ID resolved
 * through two code paths); dedup here keeps the flow resilient to that.
 */
export const deriveTicketIdsFromSelections = (
  selections: MultiListingSelectionRow[] | null | undefined
): number[] => {
  if (!selections || selections.length === 0) return [];
  const flat = selections.flatMap((sel) => {
    if (sel.qtyToSell <= 0) return [];

    // When a seat range is selected, filter to tickets within that range
    // so the submitted IDs match the user's seat selection.
    if (
      (sel.seatFrom || sel.seatTo) &&
      sel.availableTicketSeats?.length === sel.availableTicketIds.length
    ) {
      const matchingIds = sel.availableTicketIds.filter((_, i) =>
        isSeatInRange(sel.availableTicketSeats[i], sel.seatFrom, sel.seatTo)
      );
      return matchingIds.slice(0, sel.qtyToSell);
    }

    return sel.availableTicketIds.slice(0, sel.qtyToSell);
  });
  return Array.from(new Set(flat));
};

/**
 * Shared external-payment field mapping for both the single-listing
 * ({@link buildRetailSaleInput}) and multi-listing ({@link buildRetailSaleGroupInput})
 * builders. When `includeExternalFields` is false (or the payment method is not an
 * external/credit choice) all four external fields are nulled so the sale/group is
 * created Unpaid.
 */
const buildExternalPaymentFields = (
  values: RetailSaleFormValues,
  includeExternalFields: boolean
) => {
  // POS-8726: 'credit' reuses the external payment path with CustomerCredit method type
  const isExternal =
    values.paymentMethodChoice === 'external' ||
    values.paymentMethodChoice === 'credit';
  // POS-8726: For credit payments, compute the amount at submission time from the
  // current form values so it reflects any quantity/listing changes made after
  // selecting the payment method. External payments use the user-entered amount.
  // Round the computed credit amount to cents: getPaymentAmount sums line
  // amounts as JS floats and can carry a sub-cent artifact.
  const paymentAmount =
    values.paymentMethodChoice === 'credit'
      ? roundTo2(getPaymentAmount(values))
      : (values.externalPaymentAmount ?? null);

  return isExternal && includeExternalFields
    ? {
        externalPaymentMethodType: values.externalPaymentMethodType ?? null,
        externalPaymentNote: values.externalPaymentNote ?? null,
        externalPaymentAmount: paymentAmount,
        externalPaymentDate: values.externalPaymentDate ?? null,
      }
    : {
        externalPaymentMethodType: null,
        externalPaymentNote: null,
        externalPaymentAmount: null,
        externalPaymentDate: null,
      };
};

/**
 * Shared line-item mapping for both retail-sale builders. Filters out zero-amount
 * lines and normalizes signed in-form amounts/rates to positive magnitudes (the
 * paymentLineType conveys Charge-vs-Credit sign).
 */
const buildRetailLineItems = (
  values: RetailSaleFormValues
): RetailSaleLineItemInput[] | null =>
  values.marketplacePaymentLines
    ?.filter((line) => line.amount !== 0)
    .map((line) => ({
      paymentLineType: line.paymentLineType,
      // Persist a positive magnitude — the paymentLineType (Charge vs
      // Credit) is the source of truth for whether the amount is added or
      // subtracted. In-form amounts are signed (charge +, credit −) so the
      // net totals compute correctly, so normalize on the way to the DB.
      amount: Math.abs(line.amount),
      // Rate % derives from the (signed) amount, so normalize it to a
      // positive magnitude too, consistent with the stored amount.
      ratePercent: line.ratePercent != null ? Math.abs(line.ratePercent) : null,
      reference: line.externalPaymentId || null,
      description: line.description || null,
      saleLineItemChargeReason: line.saleLineItemChargeReason ?? null,
      saleLineItemCreditReason: line.saleLineItemCreditReason ?? null,
    })) ?? null;

/**
 * Builds the CreateRetailSaleGroupInput for the MULTI-listing retail-sale flow (POS-8649).
 * Ticket IDs are derived from `values.perListingSelections` via
 * {@link deriveTicketIdsFromSelections}. A single-listing checkout must use
 * {@link buildRetailSaleInput} + `SaleClient.createRetailSale` instead — the group
 * endpoint now rejects a single listing (POS-9097).
 */
export const buildRetailSaleGroupInput = (
  values: RetailSaleFormValues,
  listing: ListingDetails | undefined,
  selectedBuyer: BuyerWithSalesDto | undefined,
  includeExternalFields = true
): CreateRetailSaleGroupInput => {
  const perListingSelections = values.perListingSelections;
  const isMultiListing =
    perListingSelections != null && perListingSelections.length > 0;

  const ticketIds = isMultiListing
    ? deriveTicketIdsFromSelections(perListingSelections)
    : resolveSaleTicketIds(listing, values);

  // POS-9099: placeholder (seat-saver) members have no tickets — emit them as per-listing
  // quantity selections instead. deriveTicketIdsFromSelections already excludes these
  // (their availableTicketIds is empty).
  const placeholderSelections = (perListingSelections ?? [])
    .filter((s) => s.isPlaceholder && s.qtyToSell > 0)
    .map((s) => ({ listingId: s.listingId, quantitySold: s.qtyToSell }));

  return {
    ticketIds,
    placeholderSelections:
      placeholderSelections.length > 0 ? placeholderSelections : null,
    salesGroupName: null,
    saleDate: values.saleDate ?? null,
    currencyCode: values.currencyCode ?? null,
    inHandDate: values.inHandDate ?? null,
    // For sale groups, don't propagate the form's ticketType — each member sale
    // should retain the ticket type from its own listing.
    ticketType: isMultiListing
      ? null
      : ((values.ticketType as TicketType) ?? null),
    buyerName: selectedBuyer?.buyerName ?? null,
    buyerEmail: selectedBuyer?.buyerEmail ?? null,
    buyerPhone: selectedBuyer?.buyerPhoneNumber ?? null,
    internalNotes: values.privateNotes ?? null,
    saleStatus: PointOfSaleSaleStatus.Hold,
    totalNetProceeds: values.totalNetProceeds ?? null,
    lineItems: buildRetailLineItems(values),
    isNoFulfill: values.isNoFulfill ?? null,
    ...buildExternalPaymentFields(values, includeExternalFields),
  };
};

/**
 * Builds the CreateRetailSaleInput for the dedicated SINGLE-listing retail-sale flow
 * (POS-9097). The result is a TRUE single sale (no group-of-one): its SalesGroupId
 * stays null and its MarketplacePayment is created at the sale level.
 *
 * Mirrors {@link buildRetailSaleGroupInput}'s buyer/payment/line-item/status mapping
 * (via the shared {@link buildExternalPaymentFields} / {@link buildRetailLineItems}
 * helpers) but targets one listing: `listingId` comes from the listing and the tickets
 * to sell are inferred from the form's section/row/seat selection via
 * {@link resolveSaleTicketIds}. When `includeExternalFields` is false the external
 * fields are nulled so the sale is created Unpaid (partial/Adyen paths settle it after).
 */
/**
 * The distinct listings actually being sold, used to route between the dedicated single-sale
 * endpoint (CreateRetailSale) and the multi-listing group endpoint (CreateRetailSaleGroup) —
 * POS-9097. This is a COUNT of real listings, not a modal-mode flag: a multi-listing checkout
 * whose selection collapses to one listing must still use the single endpoint (the group
 * endpoint rejects a single listing with a 400).
 *   - Multi-listing form: the distinct `listingId`s among per-listing selections with qtyToSell > 0.
 *   - Single-listing form (no perListingSelections): the one context/form listing.
 */
export const getEffectiveListingIds = (
  values: RetailSaleFormValues,
  listing: ListingDetails | undefined
): number[] => {
  const selections = values.perListingSelections;
  if (selections != null && selections.length > 0) {
    return Array.from(
      new Set(selections.filter((s) => s.qtyToSell > 0).map((s) => s.listingId))
    );
  }
  const id = listing?.id ?? values.listingId;
  return id != null ? [id] : [];
};

export const buildRetailSaleInput = (
  values: RetailSaleFormValues,
  listing: ListingDetails | undefined,
  selectedBuyer: BuyerWithSalesDto | undefined,
  includeExternalFields = true
): CreateRetailSaleInput => {
  // This builder is only used when the sale resolves to exactly ONE listing (see
  // getEffectiveListingIds routing in the hooks). Tickets and the listing id may come
  // from either the single-listing form or a multi-listing form that collapsed to one
  // listing, so derive both from whichever source is populated.
  const perListingSelections = values.perListingSelections;
  const isMultiSource =
    perListingSelections != null && perListingSelections.length > 0;
  const effectiveListingId = getEffectiveListingIds(values, listing)[0] ?? 0;
  // POS-9099: if the effective listing is a placeholder (seat-saver) row, sell by quantity
  // (no ticket ids) via CreateRetailSaleInput.quantitySold.
  const placeholderRow = (perListingSelections ?? []).find(
    (s) =>
      s.isPlaceholder && s.listingId === effectiveListingId && s.qtyToSell > 0
  );
  return {
    listingId: effectiveListingId,
    ticketIds: placeholderRow
      ? []
      : isMultiSource
        ? deriveTicketIdsFromSelections(perListingSelections)
        : resolveSaleTicketIds(listing, values),
    quantitySold: placeholderRow ? placeholderRow.qtyToSell : null,
    saleDate: values.saleDate ?? null,
    currencyCode: values.currencyCode ?? null,
    inHandDate: values.inHandDate ?? null,
    ticketType: (values.ticketType as TicketType) ?? null,
    buyerName: selectedBuyer?.buyerName ?? null,
    buyerEmail: selectedBuyer?.buyerEmail ?? null,
    buyerPhone: selectedBuyer?.buyerPhoneNumber ?? null,
    internalNotes: values.privateNotes ?? null,
    saleStatus: PointOfSaleSaleStatus.Hold,
    totalNetProceeds: values.totalNetProceeds ?? null,
    lineItems: buildRetailLineItems(values),
    marketplaceSaleId: values.externalSaleId ?? null,
    isNoFulfill: values.isNoFulfill ?? null,
    ...buildExternalPaymentFields(values, includeExternalFields),
  };
};

export const getPaymentAmount = (values: RetailSaleFormValues) => {
  const { totalNetProceeds, marketplacePaymentLines } = values;
  const marketplaceLinesCost = (marketplacePaymentLines ?? []).reduce(
    (ret, line) => {
      ret += line.amount ?? 0;
      return ret;
    },
    0
  );
  return (totalNetProceeds ?? 0) + marketplaceLinesCost;
};

export const getPaymentCountryCode = (
  selectedBuyer: BuyerWithSalesDto | undefined,
  knownCountries?: Record<string, string> | null
) => {
  const nameToCountryCode = Object.entries(knownCountries ?? {}).reduce(
    (acc, [code, name]) => {
      acc[name] = code;
      return acc;
    },
    {} as Record<string, string>
  );
  const contryName =
    selectedBuyer?.billingCountry ?? selectedBuyer?.shippingCountry;
  if (contryName) {
    // incase the country names are set as code
    if (isValidCountryCode(contryName)) {
      return contryName;
    }
    if (nameToCountryCode[contryName]) {
      const countryCode = nameToCountryCode[contryName];
      if (isValidCountryCode(countryCode)) {
        return countryCode;
      }
    }
  }

  return 'US';
};

/**
 * Proportionally distributes a partial payment amount across all unpaid
 * marketplace payment lines. Each line receives an amount proportional to
 * its share of the total unpaid balance. Rounding is handled per-line with
 * the remainder absorbed by the last line so the sum equals `partialAmount`
 * exactly.
 *
 * @param partialAmount  The total partial payment amount to distribute.
 * @param salesData      Array of sale DTOs (from useGetSalesInSalesGroup)
 *                       whose `mkpLines` will be filtered for unpaid lines.
 * @returns              SaleLineItemCoverage[] ready to send to the API.
 */
export const computeProportionalCoverages = (
  partialAmount: number,
  salesData: {
    // Cancelled member sales (cancellationReason != null) are excluded from the
    // allocation — they're already excluded from the group's outstanding amount,
    // so paying their still-Unpaid lines would diverge from remainingAmount and
    // trip the backend's "amount === sum(coverages)" guard.
    cancellationReason?: SaleCancellationReason | null;
    mkpLines?:
      | { id: number; state: MarketplacePaymentState; amt: { amt: number } }[]
      | null;
  }[]
): SaleLineItemCoverage[] => {
  const unpaidLines = salesData
    .filter((sale) => sale.cancellationReason == null)
    .flatMap((sale) => sale.mkpLines ?? [])
    .filter(
      (line) =>
        line.state === MarketplacePaymentState.Unpaid && line.amt.amt > 0
    );

  if (unpaidLines.length === 0) return [];

  const totalUnpaid = unpaidLines.reduce((sum, l) => sum + l.amt.amt, 0);
  if (totalUnpaid <= 0) return [];

  const coverages: SaleLineItemCoverage[] = [];
  let allocated = 0;

  for (let i = 0; i < unpaidLines.length; i++) {
    const line = unpaidLines[i];
    const isLast = i === unpaidLines.length - 1;

    if (isLast) {
      // Last line absorbs any rounding remainder, capped at the line's amount
      const remainder = roundTo2(partialAmount - allocated);
      const capped = Math.min(remainder, line.amt.amt);
      if (capped > 0) {
        coverages.push({
          marketplacePaymentLineId: line.id,
          amountToPay: capped,
        });
      }
    } else {
      const proportion = line.amt.amt / totalUnpaid;
      // Cap at the line's amount so rounding never exceeds what's owed
      const amount = Math.min(
        roundTo2(partialAmount * proportion),
        line.amt.amt
      );
      allocated += amount;
      if (amount > 0) {
        coverages.push({
          marketplacePaymentLineId: line.id,
          amountToPay: amount,
        });
      }
    }
  }

  return coverages;
};
